import React from 'react';
import {BrowserRouter, Navigate, Outlet, Route, Routes, useLocation} from "react-router";
import {useContext} from "react";
import {AuthContext} from "../context/AuthContext";
import {hasPermission} from "../utils/permissionUtils";
import Login from "../pages/authentication/Login";
import Dashboard from "../pages/dashboard/Dashboard";
import Reservations from "../pages/reservation/Reservations";
import Categories from "../pages/category/Categories";
import Services from "../pages/category/service/Services";
import ServiceDetail from "../pages/category/service/ServiceDetail";
import Colleagues from "../pages/colleague/Colleagues";
import ColleagueDetail from "../pages/colleague/ColleagueDetail";
import Settings from "../pages/settings/Settings";
import Web from "../pages/website/Web";
import Notifications from "../pages/notification/Notifications";
import OpeningHours from "../pages/opening-hours/OpeningHours";
import ExtraItems from "../pages/extra-items/ExtraItems";
import ExtraItemDetail from "../pages/extra-items/ExtraItemDetail";
import Orders from "../pages/order/Orders";
import ForgotPassword from "../pages/authentication/ForgotPassword";
import NewPassword from "../pages/authentication/NewPassword";
import ActivationLogin from "../pages/authentication/ActivationLogin";
import Register from "../pages/authentication/Registration";
import RegisterExisting from "../pages/authentication/ExistingRegistration";
import CardRegistration from "../pages/authentication/CardRegistration";
import PaymentOver from "../pages/authentication/PaymentOver";
import SmsPaymentOver from "../pages/authentication/SmsPaymentOver";
import SuccessfulRegistration from "../pages/authentication/SuccessfulRegistration";
import {SubscriptionProvider} from "../context/SubscriptionContext";
import {AnnouncementProvider} from "../context/AnnouncementContext";
import Wizard from "../pages/wizard/Wizard";
import {ErrorBoundary} from "react-error-boundary";
import GeneralError from "../pages/GeneralError";


function PublicRoute() {
    const {auth} = useContext(AuthContext);

    if (auth?.access) {
        return <Navigate to="/" replace/>;
    }

    return <Outlet/>;
}

function PrivateRoute() {
    const {auth, loading} = useContext(AuthContext);
    const location = useLocation();

    if (loading) return null

    // If no access token, redirect to login
    if (!auth?.access) {
        return <Navigate to="/login" state={{from: location}} replace/>;
    }

    // If logged in, render the requested page
    return <SubscriptionProvider>
        <AnnouncementProvider>
            <ErrorBoundary fallback={<GeneralError />}>
                <Outlet/>
            </ErrorBoundary>
        </AnnouncementProvider>
    </SubscriptionProvider>
}

const defaultRoutes = {
    categories: "/categories",
    colleagues: "/colleagues",
    settings: "/settings",
    web: "/web",
    notifications: "/notifications",
    'opening-hours': "/opening-hours",
    'extra-items': "/extra-items",
    reservations: "/reservations",
    dashboard: "/",
};

function getNextAllowedRoute(user) {
    const resources = ["dashboard", "reservations", "categories"]; // order defines priority

    for (const res of resources) {
        if (hasPermission(user, res, "view")) {
            return defaultRoutes[res];
        }
    }
    return "/"; // fallback if none
}


const ProtectedRoute = ({resource, action = "view", data = null}) => {
    const {getUser} = useContext(AuthContext);
    const user = getUser();
    // No permission → redirect to dashboard or 403
    const allowed = hasPermission(user, resource, action, data);

    if (!allowed) {
        const nextPage = getNextAllowedRoute(user);
        return <Navigate to={nextPage} replace/>;
    }

    // OK → show the page
    return <Outlet/>;
}

const AppRoutes = () => {
    return (
        <Routes>

            {/* Public routes */}
            <Route element={<PublicRoute/>}>
                <Route path="/login" element={<Login/>}/>
                <Route path="/forgot-password" element={<ForgotPassword/>}/>
                <Route path="/new-password/:slug" element={<NewPassword/>}/>
                <Route path="/activation-login" element={<ActivationLogin/>}/>
                <Route path="/new-password/:slug" element={<NewPassword/>}/>
                <Route path="/register" element={<Register/>}/>
                <Route path="/register-existing/:slug" element={<RegisterExisting/>}/>
                {/*<Route path="/card-registration" element={<CardRegistration/>}/>*/}

                <Route path="/success" element={<SuccessfulRegistration/>}/>
            </Route>

            {/* Private routes */}
            <Route element={<PrivateRoute/>}>
                {/* reservations */}
                <Route element={<ProtectedRoute resource="reservation" action="view"/>}>
                    <Route path="/" element={<Dashboard/>}/>
                    <Route path="/reservations" element={<Reservations/>}/>
                </Route>

                {/* categories / services */}
                <Route element={<ProtectedRoute resource="categories" action="view"/>}>
                    <Route path="/categories" element={<Categories/>}/>
                    <Route path="/categories/:categoryId" element={<Services/>}/>
                    <Route path="/categories/:categoryId/:serviceId" element={<ServiceDetail/>}/>
                </Route>

                {/* colleagues */}
                <Route element={<ProtectedRoute resource="colleagues" action="view"/>}>
                    <Route path="/colleagues" element={<Colleagues/>}/>
                    <Route path="/colleagues/:colleagueId" element={<ColleagueDetail/>}/>
                </Route>

                {/* settings */}
                <Route element={<ProtectedRoute resource="settings" action="view"/>}>
                    <Route path="/settings" element={<Settings/>}/>
                    <Route path="/payment-over" element={<PaymentOver/>}/>
                    <Route path="/sms-payment-over" element={<SmsPaymentOver/>}/>
                </Route>

                {/* website */}
                <Route element={<ProtectedRoute resource="business" action="view"/>}>
                    <Route path="/web" element={<Web/>}/>
                </Route>

                {/* notifications */}
                <Route element={<ProtectedRoute resource="notifications" action="view"/>}>
                    <Route path="/notifications" element={<Notifications/>}/>
                </Route>

                {/* opening-hours */}
                <Route element={<ProtectedRoute resource="opening-hours" action="update"/>}>
                    <Route path="/opening-hours" element={<OpeningHours/>}/>
                </Route>

                {/* extra-items */}
                <Route element={<ProtectedRoute resource="extra-items" action="view"/>}>
                    <Route path="/extra-items" element={<ExtraItems/>}/>
                    <Route path="/extra-items/:extraItemId" element={<ExtraItemDetail/>}/>
                </Route>

                {/* orders */}
                <Route element={<ProtectedRoute resource="orders" action="view"/>}>
                    <Route path="/orders" element={<Orders/>}/>
                    {/*<Route path="/extra-items/:extraItemId" element={<ExtraItemDetail />}/>*/}
                </Route>

                {/* wizard */}
                <Route element={<ProtectedRoute resource="settings" action="view"/>}>
                    <Route path="/wizard" element={<Wizard/>}/>
                </Route>

            </Route>

            {/* Redirects */}
            <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
    )
};

export default AppRoutes;